// Value formatting shared by tables, detail pages, history diffs and CSV export.
import { format, formatDistanceStrict, isValid, parseISO } from 'date-fns'
import type { FieldSchema } from './schema'

const ACRONYMS = new Set([
  'SOC',
  'SOC2',
  'SOC3',
  'HIPAA',
  'URL',
  'API',
  'MFA',
  'NDA',
  'BAA',
  'DPA',
  'ISO',
  'PII',
  'PHI',
  'HR',
  'IT',
  'AI',
  'NIST',
  'SSO',
  'CSV',
  'PDF',
  'ID',
  'IDP',
  'SLA',
  'DCF',
  'CVE',
  'CVSS',
  'VM',
  'OS',
  'KB',
  'UAR',
])

const WORDS: Record<string, string> = {
  MICROSOFT: 'Microsoft',
  AZURE: 'Azure',
  INTUNE: 'Intune',
  DEFENDER: 'Defender',
  GITHUB: 'GitHub',
  CHECKR: 'Checkr',
  ENTRA: 'Entra',
  DRATA: 'Drata',
  SAFEBASE: 'SafeBase',
  ZOHO: 'Zoho',
  TEAMS: 'Teams',
  DEPENDABOT: 'Dependabot',
}

/** NEEDS_ARTIFACT -> "Needs artifact", MICROSOFT_365 -> "Microsoft 365", SOC2_TYPE2 -> "SOC2 type2". */
export function humanizeEnum(value: string): string {
  if (!value) return ''
  const words = value.split(/[_\s]+/).filter(Boolean)
  return words
    .map((w, i) => {
      const up = w.toUpperCase()
      if (ACRONYMS.has(up)) return up
      if (WORDS[up]) return WORDS[up]
      const lower = w.toLowerCase()
      return i === 0 ? lower.charAt(0).toUpperCase() + lower.slice(1) : lower
    })
    .join(' ')
}

// Display labels for enum values. The server declares them per field (packages/schema f.enum `labels`, served by
// GET /api/v1/_schema as `labels` and in OpenAPI as x-enum-labels), for example evidence status NEEDS_RENEWAL shows as
// "Upcoming renewal" the way Drata names it. Values without a declared label are humanized (NEEDS_ARTIFACT ->
// "Needs artifact"). The stored value never changes: filters and writes keep using it. They live here, next to
// humanizeEnum, so formatPlain can use them; components/fields/enum-labels.ts re-exports them for the field widgets.

export type EnumLabels = Readonly<Record<string, string>>

export interface EnumOption {
  value: string
  label: string
}

/**
 * The labels a schema field carries, when the server sent them (older payloads and non-enum fields have none).
 * Takes any field object: the labels are read defensively, whatever the field type declares.
 */
export function enumLabelsOf(field: object | null | undefined): EnumLabels | undefined {
  const raw = field && 'labels' in field ? (field as { labels?: unknown }).labels : undefined
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined
  const out: Record<string, string> = {}
  for (const [value, label] of Object.entries(raw))
    if (typeof label === 'string' && label.trim()) out[value] = label
  return Object.keys(out).length ? out : undefined
}

/** How people see one enum value: the declared label, else the humanized value. */
export function enumLabel(value: string, labels?: EnumLabels): string {
  return labels?.[value] ?? humanizeEnum(value)
}

/** The field's values in declared order, each with its display label (selects, filter menus). */
export function enumOptions(field: { values?: readonly string[]; labels?: unknown }): EnumOption[] {
  const labels = enumLabelsOf(field)
  return (field.values ?? []).map((value) => ({ value, label: enumLabel(value, labels) }))
}

export type Tone = 'success' | 'danger' | 'warning' | 'info' | 'neutral' | 'brand'

const TONES: Array<[Tone, RegExp]> = [
  [
    'danger',
    /^(FAILED|FAILURE|FAIL|ERROR|EXPIRED|NOT_READY|OVERDUE|PAST_DUE|CRITICAL|HIGH|REJECTED|DENIED|AUTO_DENIED|NONCOMPLIANT|ADVERSE|REVOKE|REVOKED|DISCONNECTED|MISCONFIGURED|MISSING|NEEDS_RENEWAL|CHANGES_REQUESTED)$/,
  ],
  [
    'warning',
    /^(NEEDS_.*|PENDING|EXPIRING_SOON|IN_REVIEW|MEDIUM|MODERATE|OUTSTANDING|INCOMPLETE|CONSIDER|NEEDS_APPROVAL|IDENTIFIED|IN_TREATMENT|REQUIRED|QUEUED|PREPARING|APPROVED_WITH_CONDITIONS|QUALIFIED|SUSPENDED)$/,
  ],
  [
    'success',
    /^(READY|PASSED|PASS|ACTIVE|PUBLISHED|APPROVED|AUTO_APPROVED|COMPLETED|COMPLETE|ACCEPTED|COMPLIANT|SUCCESS|DONE|CURRENT|SIGNED|SIGNED_OUTSIDE|CLEAR|RESOLVED|KEEP|SENT|PREPARED|SUBSCRIBED|UNQUALIFIED|FULL)$/,
  ],
  [
    'info',
    /^(IN_PROGRESS|NEW|OPEN|TESTING|PLANNED|DRAFT|PROSPECTIVE|NOT_STARTED|INFO|NORMAL|SCHEDULED|MANUAL)$/,
  ],
]

/** Badge color for an enum value (Drata convention: green good, red failing, amber needs attention). */
export function enumTone(value: string): Tone {
  const v = value.toUpperCase()
  for (const [tone, re] of TONES) if (re.test(v)) return tone
  return 'neutral'
}

/** Parses API dates. Date-only values ("2026-07-15") are read as local dates, not UTC midnight. */
export function parseDate(value: unknown): Date | null {
  if (value instanceof Date) return isValid(value) ? value : null
  if (typeof value !== 'string' || !value) return null
  const d = parseISO(value)
  return isValid(d) ? d : null
}

export function formatDate(value: unknown): string {
  const d = parseDate(value)
  return d ? format(d, 'MMM d, yyyy') : ''
}

export function formatDateTime(value: unknown): string {
  const d = parseDate(value)
  return d ? format(d, 'MMM d, yyyy h:mm a') : ''
}

/** "3 days ago", "in 2 months". */
export function formatRelative(value: unknown, now: Date = new Date()): string {
  const d = parseDate(value)
  if (!d) return ''
  const diff = Math.abs(d.getTime() - now.getTime())
  if (diff < 45_000) return 'just now'
  const distance = formatDistanceStrict(d, now, { roundingMethod: 'floor' })
  return d.getTime() < now.getTime() ? `${distance} ago` : `in ${distance}`
}

export function formatNumber(value: unknown): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return ''
  return value.toLocaleString('en-US')
}

export function truncate(s: string, max = 80): string {
  return s.length > max ? `${s.slice(0, max - 3)}...` : s
}

export function shortId(id: string): string {
  return id.length > 8 ? `${id.slice(0, 8)}...` : id
}

/**
 * Plain-text rendering of a field value (CSV export, history diffs, tooltips).
 * refDisplay resolves ref ids to display names when known. Enum values use the field's declared display labels
 * (Drata's wording, e.g. "Upcoming renewal"), so a CSV export reads like the screen.
 */
export function formatPlain(
  field: Pick<FieldSchema, 'kind' | 'sensitive' | 'labels'>,
  value: unknown,
  refDisplay?: (id: string) => string | undefined,
): string {
  if (value === null || value === undefined || value === '') return ''
  switch (field.kind) {
    case 'boolean':
      return value === true ? 'Yes' : value === false ? 'No' : String(value)
    case 'enum':
      return enumLabel(String(value), enumLabelsOf(field))
    case 'date':
      return typeof value === 'string' ? value.slice(0, 10) : String(value)
    case 'datetime': {
      const d = parseDate(value)
      return d ? d.toISOString() : String(value)
    }
    case 'int':
    case 'number':
      return typeof value === 'number' ? String(value) : String(value)
    case 'tags':
      return Array.isArray(value) ? value.join('; ') : String(value)
    case 'ref':
    case 'file':
      return refDisplay?.(String(value)) ?? String(value)
    case 'refs':
      return Array.isArray(value)
        ? value.map((id) => refDisplay?.(String(id)) ?? String(id)).join('; ')
        : String(value)
    case 'json':
      return typeof value === 'string' ? value : JSON.stringify(value)
    default:
      return String(value)
  }
}
